import { Paper, Typography } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import Holidays from 'date-holidays';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getReportTypeLabel, permissionLevelEnum, reportTypeEnum, systemConfigKeyEnum } from '../../../../constants';
import { getReportTypeColor } from '../../../../constants/colors';
import { siteTypeEnum } from '../../../../constants/enums/site';
import { useMonthlyReportsByUserQuery } from '../../../../hooks/queries/useReportsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { useTheme } from '../../../../hooks/useTheme';
import { ReportWithActivities } from '../../../../services/apiSLTP/dailyReports/reports';
import { Site } from '../../../../services/apiSLTP/sites/sites';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatDateToDDMMYYYY, formatTime } from '../../../../utils/dates';
import { formatNumber } from '../../../../utils/numbers';
import StateCell from '../../../cells/StateCell';
import ModalBody from '../../../modals/ModalBody';
import GenericAgGridTable from '../../GenericAgGridTable';
const hd = new Holidays('FR');

interface MonthlyReportsTableProps {
    currentDate: Date;
}

const MonthlyReportsTable: React.FC<MonthlyReportsTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme } = useTheme();
    const { getConfigValue } = useSystemConfig();
    const [selectedReport, setSelectedReport] = useState<ReportWithActivities | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { data: dataMonthlyReports, isLoading: isMonthlyReportsLoading } = useMonthlyReportsByUserQuery({
        params: { date: currentDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days: { key: string; label: string; date: Date }[] = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const numDays = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= numDays; day++) {
            const d = new Date(year, month, day);
            days.push({
                key: dayjs(d).format('YYYY-MM-DD'),
                label: d.getDate().toString(),
                date: d,
            });
        }
        return days;
    }, [currentDate]);

    const rowData = useMemo(() => {
        if (!dataMonthlyReports) return [];

        return dataMonthlyReports
            .filter((userWithReports) => !userWithReports.user.isSpecial)
            .map((userWithReports) => {
                const user = userWithReports.user;
                const row: Record<string, any> = {
                    name: formatName(user.firstName, user.lastName),
                    codeProC: user.codeProC,
                    _id: user._id,
                    userId: user._id,
                    jobHasTravelZones: user.jobId?.hasTravelZones ?? false,
                };

                const reportsByDateMap = new Map<string, ReportWithActivities>();
                userWithReports.reportsByDate.forEach((reportByDate) => {
                    reportsByDateMap.set(reportByDate.date, reportByDate.report);
                });

                daysInMonth.forEach(({ key }) => {
                    row[key] = reportsByDateMap.get(key) || null;
                });

                row.prodDaysCount = userWithReports.reportsByDate.filter(
                    (reportByDate) => reportByDate.report.reportType === reportTypeEnum.SITE && reportByDate.report.activities.length > 0
                ).length;

                row.totalExtraHours = userWithReports.reportsByDate.reduce((total, reportByDate) => total + (reportByDate.report.extraHours || 0), 0);

                return row;
            })
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [dataMonthlyReports, daysInMonth]);

    const getFirstSite = (report: ReportWithActivities): Site | null => {
        if (!report.activities.length) return null;
        const sortedActivities = [...report.activities.filter((activity) => activity.siteId?.type !== siteTypeEnum.EXTERNAL)].sort(
            (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );
        return sortedActivities[0]?.siteId || null;
    };

    const hasTrainingSite = (report: ReportWithActivities): boolean => {
        return report.activities.some((activity) => activity.siteId?.type === siteTypeEnum.TRAINING);
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'name',
                colId: 'name',
                initialPinned: 'left',
            },
            {
                headerName: capitalizeFirstLetter(t('codeProC')),
                field: 'codeProC',
                colId: 'codeProC',
                initialWidth: 150,
                initialPinned: 'left',
            },
            {
                headerName: capitalizeFirstLetter(t('prodDays')),
                field: 'prodDaysCount',
                colId: 'prodDaysCount',
                initialPinned: 'left',
                initialWidth: 150,
                type: 'numericColumn',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: `${capitalizeFirstLetter(t('phoneAllowance'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
                valueGetter: (params) => getConfigValue(systemConfigKeyEnum.PAYROLL_PHONE_ALLOWANCE_AMOUNT) * params.data?.prodDaysCount || '-',
                valueFormatter: (params) => {
                    const amount = params.value;
                    return typeof amount === 'number' ? `${formatNumber(amount, t('locale'))}` : '-';
                },
                colId: 'phoneAllowance',
                type: 'numericColumn',
                filter: 'agNumberColumnFilter',
                initialPinned: 'left',
            },
            {
                headerName: capitalizeFirstLetter(t('extraHours')),
                field: 'totalExtraHours',
                colId: 'totalExtraHours',
                initialPinned: 'left',
                type: 'numericColumn',
                valueFormatter: (params) => {
                    const hours = params.value ?? 0;
                    return hours !== 0 ? (hours > 0 ? `+${hours}h` : `${hours}h`) : '-';
                },
                filter: 'agNumberColumnFilter',
            },
        ];

        const dateCols: ColDef[] = daysInMonth.map(({ key, label, date }) => ({
            headerName: label,
            colId: key,
            field: key,
            initialWidth: 150,
            cellClass: 'ag-cell-remove-of',
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            valueFormatter: (params) => {
                const report: ReportWithActivities | null = params.data[key];
                if (!report) return t('n/a').toUpperCase();
                if (report.activities.some((activity) => activity.siteId?.type === siteTypeEnum.TRAINING)) {
                    return `${capitalizeFirstLetter(t(getReportTypeLabel(report.reportType)))} - ${capitalizeFirstLetter(t('training'))}`;
                }
                return capitalizeFirstLetter(t(getReportTypeLabel(report.reportType)));
            },
            headerClass: hd.isHoliday(date) ? 'text-error' : date.getDay() === 6 ? 'text-warning' : date.getDay() === 0 ? 'text-error' : undefined,
            cellRenderer: (params: ICellRendererParams) => {
                const report: ReportWithActivities | null = params.data[key];
                const jobHasTravelZones = params.data.jobHasTravelZones;

                if (!report || (report.reportType === reportTypeEnum.SITE && report.activities.length === 0)) {
                    return <div className="text-gray">{t('n/a').toUpperCase()}</div>;
                }

                const firstSite = getFirstSite(report);
                const isTraining = hasTrainingSite(report);
                const extraHours = report.extraHours ?? 0;

                return (
                    <div className="fx-r fx-items-c gap-10px">
                        <StateCell
                            text={capitalizeFirstLetter(t(getReportTypeLabel(report.reportType))).substring(0, 3)}
                            stateColor={getReportTypeColor(report.reportType)}
                            showDot={false}
                        />

                        {report.reportType === reportTypeEnum.SITE && report.activities.length > 0 && (
                            <Paper
                                style={{
                                    display: 'flex',
                                    minWidth: 25,
                                    height: 25,
                                    borderRadius: '50%',
                                    backgroundColor: report.isLongTrip ? 'gold' : isTraining ? 'blue' : undefined,
                                    color: report.isLongTrip ? muiTheme.palette.common.black : muiTheme.palette.text.primary,
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    fontWeight: 'bold',
                                    padding: '0 5px',
                                }}
                                variant="outlined"
                                onClick={() => {
                                    setSelectedReport(report);
                                    setIsDialogOpen(true);
                                }}
                            >
                                {report.isLongTrip ? 'GD' : isTraining ? 'F' : jobHasTravelZones ? firstSite?.workTravelZoneId?.shortName || '?' : ''}
                            </Paper>
                        )}

                        {extraHours !== 0 && (
                            <Typography fontSize="0.75rem" fontWeight="bold" color={extraHours > 0 ? 'success.main' : 'error.main'}>
                                {extraHours > 0 ? `+${extraHours}h` : `${extraHours}h`}
                            </Typography>
                        )}
                    </div>
                );
            },
        }));

        const adminCols: ColDef[] =
            user?.profileId.permsLevel === permissionLevelEnum.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...dateCols, ...adminCols];
    }, [t, daysInMonth, user, muiTheme]);

    const getUniqueSitesFromReport = (report: ReportWithActivities): Site[] => {
        const sitesMap = new Map<string, Site>();
        report.activities.forEach((activity) => {
            if (activity.siteId && activity.siteId._id) {
                sitesMap.set(activity.siteId._id, activity.siteId);
            }
        });
        return Array.from(sitesMap.values());
    };

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`presence-users-${dayjs(currentDate).format('YYYY-MM')}`}
                agGridProps={{ getRowId: ({ data }) => data.userId }}
                loading={isMonthlyReportsLoading}
            />

            <ModalBody
                open={isDialogOpen}
                handleClose={() => {
                    setIsDialogOpen(false);
                    setSelectedReport(null);
                }}
                title={formatName(selectedReport?.userId.firstName, selectedReport?.userId.lastName)}
                subtitle={selectedReport?.date ? formatDateToDDMMYYYY(new Date(selectedReport.date), '/') : undefined}
                showSubmitButton={false}
                width={600}
            >
                {selectedReport && (
                    <div className="fx-c gap-5px">
                        <Typography>{capitalizeFirstLetter(t(getReportTypeLabel(selectedReport.reportType)))}</Typography>

                        {selectedReport.isLongTrip && <Typography>{capitalizeFirstLetter(t('longTrip'))}</Typography>}
                        {selectedReport.activities.length > 0 &&
                            selectedReport.activities.map((activity, index) => (
                                <Typography key={index}>
                                    {formatTime(new Date(activity.startTime))} - {activity.activityTypeId.designation} -{' '}
                                    {activity.siteId?.name || t('noName')} - {activity.siteId?.workTravelZoneId?.name || t('noTravelZone')}
                                </Typography>
                            ))}
                    </div>
                )}
            </ModalBody>
        </>
    );
};

export default MonthlyReportsTable;
